import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ContactUsModalComponent } from '../../shared/contact-modal/contact-modal.component'; // adjust path
import {
  IneligibilityDetails,
  NavigationService,
} from "../../services/navigation.service";
import { LocaleService } from "../../services/locale.service";
import { ContentService } from "../../services/content.service";
import { Subscription } from "rxjs";

const regretScreenCopy = {
  en: {
    heading: "Thank you for your willingness to help.",
    intro:
      "We deeply appreciate your willingness to contribute to this life-saving cause.",
    fallbackMessage: "You are ineligible to proceed.",
    outro:
      "Please know that your desire to make a difference is truly valued, and there are still many other ways you can support those in need. If you have any questions or would like to explore other opportunities, we're here to help.",
    weightToLoseBefore: "You would need to lose about",
    weightToLoseAfter: "lbs to meet the BMI threshold.",
    contactPrompt: "Something wrong with the results?",
    contactLink: "Contact Us",
  },
  es: {
    heading: "Gracias por su disposición a ayudar.",
    intro:
      "Apreciamos profundamente su disposición a contribuir a esta causa que salva vidas.",
    fallbackMessage: "No es elegible para continuar.",
    outro:
      "Sepa que su deseo de marcar una diferencia es realmente valioso, y todavía hay muchas otras formas en que puede apoyar a quienes lo necesitan. Si tiene preguntas o desea explorar otras oportunidades, estamos aquí para ayudar.",
    weightToLoseBefore: "Tendría que perder aproximadamente",
    weightToLoseAfter: "libras para cumplir con el umbral de IMC.",
    contactPrompt: "¿Hay algo incorrecto con los resultados?",
    contactLink: "Contáctenos",
  },
};

@Component({
  selector: "app-post-initial-regret-screen",
  templateUrl: "./post-initial-regret-screen.component.html",
  styleUrl: "./post-initial-regret-screen.component.scss",
})
export class PostInitialRegretScreenComponent implements OnInit, OnDestroy {
  ineligibilityMessage: string | null = null;
  ineligibilityDetails: IneligibilityDetails | null = null;
  private sourceIneligibilityMessage: string | null = null;
  private sourceMessageLocale = "en";
  private subscriptions = new Subscription();
  private translationRequestId = 0;

  get copy() {
    return (
      regretScreenCopy[
        this.localeService.currentLocale as keyof typeof regretScreenCopy
      ] || regretScreenCopy.en
    );
  }

  constructor(
    private modalService: NgbModal,
    private navigationService: NavigationService,
    private localeService: LocaleService,
    private contentService: ContentService
  ) {}

  ngOnInit(): void {
    this.sourceMessageLocale = this.normalizeLocale(
      this.localeService.currentLocale
    );

    //  this.navigationService.ineligibilityMessage$.subscribe(
    //   (message) => {
    //     this.ineligibilityMessage = message || "You are ineligible to proceed.";
    //   }
    // );

    this.subscriptions.add(
      this.navigationService.ineligibilityDetails$.subscribe((details) => {
        this.ineligibilityDetails = details;
        this.sourceIneligibilityMessage = details?.message?.trim() || null;
        this.sourceMessageLocale = this.normalizeLocale(
          this.localeService.currentLocale
        );
        this.updateIneligibilityMessage();
      })
    );

    this.subscriptions.add(
      this.localeService.locale$.subscribe(() => {
        this.updateIneligibilityMessage();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  openContactModal(): NgbModalRef {
    return this.modalService.open(ContactUsModalComponent, {
      centered: true,
      backdrop: "static",
    });
  }

  private updateIneligibilityMessage(): void {
    const requestId = ++this.translationRequestId;
    const targetLocale = this.normalizeLocale(this.localeService.currentLocale);
    const sourceMessage = this.sourceIneligibilityMessage;

    if (!sourceMessage) {
      this.ineligibilityMessage = this.copy.fallbackMessage;
      return;
    }

    if (this.sourceMessageLocale === targetLocale) {
      this.ineligibilityMessage = sourceMessage;
      return;
    }

    this.ineligibilityMessage = this.copy.fallbackMessage;
    this.contentService
      .translateText({
        sourceLocale: this.sourceMessageLocale,
        targetLocale,
        text: sourceMessage,
        context: "Living kidney donor initial questionnaire ineligibility message",
      })
      .subscribe({
        next: (translation) => {
          if (requestId !== this.translationRequestId) {
            return;
          }

          this.ineligibilityMessage =
            translation.translatedText?.trim() || this.copy.fallbackMessage;
        },
        error: (error) => {
          if (requestId !== this.translationRequestId) {
            return;
          }

          console.error("Error translating ineligibility message:", error);
          this.ineligibilityMessage = this.copy.fallbackMessage;
        },
      });
  }

  private normalizeLocale(locale: string | null | undefined): string {
    return locale?.split("-")[0]?.trim().toLowerCase() || "en";
  }
  
}
